<template>
    <VJoyPanel class="budget-tracking">
        <template #panel-title>
            <div class="budget-tracking_header / flex">
                <div class="budget-tracking_header_title / flex">
                    <span>{{ $t('projectclient.projectview.budget.tracking.title') }}</span>
                    <VJoyTooltip variant="secondary">
                        <VJoyIcon name="question-circle" />
                        <template #tooltip-content>
                            <b>{{ $t('projectclient.projectview.budget.tracking.tooltip.total.amount', [totalAmount]) }}</b>
                            <br />
                            <br />
                            {{ $t('projectclient.projectview.budget.tracking.tooltip.total.description') }}
                        </template>
                    </VJoyTooltip>
                </div>
            </div>
        </template>
        <template #panel-body>
            <div class="budget-tracking_body / flex">
                <div class="budget-tracking_body_progress / flex">
                    <div class="budget-tracking_body_progress_bar">
                        <VJoyProgressBar :color="currentStatus" :steps="100" :current-step="currentProgress" />
                    </div>
                    <div class="budget-tracking_body_progress_badge" :class="[currentStatus]">
                        <VJoyBadge :variant="badgeVariantMapper" :bulletpoint="false">{{ currentProgress }}%</VJoyBadge>
                    </div>
                </div>
                <div class="budget-tracking_body_stats / flex">
                    <div class="budget-tracking_body_stats_item">
                        <div class="budget-tracking_body_stats_item_title / flex">
                            <div class="budget-tracking_body_stats_item_title_bulletpoint primary"></div>
                            <span>{{ t('projectclient.projectview.budget.tracking.progress.invoiced') }}</span>
                        </div>
                        <div class="budget-tracking_body_stats_item_value">
                            {{ committedValue }}
                        </div>
                        <div class="budget-tracking_body_stats_item_time">
                            {{ commitedTime }}
                        </div>
                    </div>
                    <div class="budget-tracking_body_stats_item">
                        <div class="budget-tracking_body_stats_item_title / flex">
                            <div v-if="currentStatus === 'success'" class="budget-tracking_body_stats_item_title_bulletpoint secondary"></div>
                            <span v-if="currentStatus === 'success'">
                                {{ $t('projectclient.projectview.budget.tracking.progress.remaining.default') }}
                            </span>
                            <span v-else>{{ $t('projectclient.projectview.budget.tracking.progress.remaining.error') }}</span>
                        </div>
                        <div class="budget-tracking_body_stats_item_value">
                            {{ availableValue }}
                        </div>
                        <div class="budget-tracking_body_stats_item_time">
                            {{ availableTime }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {formatDuration} from 'date-fns';
    import * as locales from 'date-fns/locale';
    import {storeToRefs} from 'pinia';
    import {useFormat, useLocale, useTranslation} from '#imports';
    import {
        type TJoyProgressBarColors,
        type TJoyBadgeVariants,
        VJoyPanel,
        VJoyTooltip,
        VJoyIcon,
        VJoyProgressBar,
        VJoyBadge,
    } from '@maltjoy/core-vue';
    import {useProjectInformationStore} from '~/common/stores';

    const {t} = useTranslation();
    const {formatAmount} = useFormat();
    const {languageCode} = useLocale();

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const currentProgress = computed(() => {
        return Math.round(projectInformation.value.budgetConsumption?.amount.percent ?? 0);
    });

    const committedValue = computed<string>(() => {
        return formatAmount(projectInformation.value.contract.currency, projectInformation.value.budgetConsumption?.amount.committed ?? 0);
    });

    const commitedTime = computed(() => {
        const commitedTime = Math.abs(projectInformation.value.budgetConsumption?.time.committed ?? 0);
        return formatDuration(
            {
                ...(projectInformation.value.freelancerWorkedUnit === 'DAY' && {days: Number((Math.round(commitedTime * 2) / 2).toFixed(1))}),
                ...(projectInformation.value.freelancerWorkedUnit === 'HOUR' && {hours: Math.floor(commitedTime)}),
            },
            {locale: (locales as any)[languageCode.value], zero: true},
        );
    });

    const transformedRemainingAmount = computed(() =>
        projectInformation.value.budgetConsumption?.amount.remaining ? Math.abs(projectInformation.value.budgetConsumption?.amount.remaining) : 0,
    );

    const availableValue = computed<string>(() => {
        return formatAmount(projectInformation.value.contract.currency, transformedRemainingAmount.value);
    });

    const availableTime = computed(() => {
        const remainingTime = Math.abs(projectInformation.value.budgetConsumption?.time.remaining ?? 0);
        return formatDuration(
            {
                ...(projectInformation.value.freelancerWorkedUnit === 'DAY' && {days: Number((Math.round(remainingTime * 2) / 2).toFixed(1))}),
                ...(projectInformation.value.freelancerWorkedUnit === 'HOUR' && {hours: Math.floor(remainingTime)}),
            },
            {locale: (locales as any)[languageCode.value], zero: true},
        );
    });

    const totalAmount = computed<string>(() => {
        return formatAmount(
            projectInformation.value.contract.currency,
            (projectInformation.value.budgetConsumption?.amount.committed ?? 0) + (projectInformation.value.budgetConsumption?.amount.remaining ?? 0),
        );
    });

    const currentStatus = computed<TJoyProgressBarColors>(() => {
        if (currentProgress.value < 100) {
            return 'success';
        } else if (currentProgress.value >= 100) {
            return 'warning';
        }
        return 'error';
    });

    const badgeVariantMapper = computed<TJoyBadgeVariants>(() => {
        if (currentStatus.value === 'success') {
            return 'green';
        } else if (currentStatus.value === 'warning') {
            return 'orange';
        }
        return 'red';
    });

    const accentColorPrimary = computed(() => {
        if (currentStatus.value === 'success') {
            return 'var(--joy-color-success-90)';
        } else if (currentStatus.value === 'warning') {
            return 'var(--joy-color-warning-50)';
        }
        return 'var(--joy-color-error-50)';
    });

    const accentColorSecondary = computed(() => {
        if (currentStatus.value === 'success') {
            return 'var(--joy-color-success-10)';
        } else if (currentStatus.value === 'warning') {
            return 'var(--joy-color-warning-10)';
        }
        return 'var(--joy-color-error-10)';
    });
</script>

<style lang="scss" scoped>
    .budget-tracking {
        :deep(.joy-panel__header) {
            align-items: center;
        }

        &_header {
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            &_title {
                flex: 1 1 auto;
                gap: var(--joy-core-spacing-2);
                align-items: center;

                & > * {
                    flex: none;
                }
            }
        }

        &_body {
            row-gap: var(--joy-core-spacing-2);

            &_progress {
                width: 100%;
                align-items: center;
                gap: var(--joy-core-spacing-4);

                &_bar {
                    flex: 1 1 auto;

                    &:deep(.joy-progress-bar-wrapper) {
                        overflow: hidden;
                    }
                }

                &_badge {
                    :deep(.joy-badge) {
                        font-weight: var(--joy-font-weight-bold);
                        color: v-bind(accentColorPrimary);
                    }
                }
            }

            &_stats {
                gap: var(--joy-core-spacing-8);

                &_item {
                    &_title {
                        align-items: center;
                        gap: var(--joy-core-spacing-1);
                        font-size: var(--joy-font-size-primary-200);
                        color: var(--joy-color-neutral-grey);

                        &_bulletpoint {
                            height: 8px;
                            width: 8px;
                            border-radius: var(--joy-core-radius-3);

                            &.primary {
                                background-color: v-bind(accentColorPrimary);
                            }

                            &.secondary {
                                background-color: v-bind(accentColorSecondary);
                            }
                        }
                    }

                    &_value {
                        margin-top: 2px;
                        font-weight: var(--joy-font-weight-bold);
                    }

                    &_time {
                        font-size: var(--joy-font-size-primary-200);
                    }
                }
            }
        }
    }
</style>
